import type { GmailConnection } from "./gmailApi";
import { resolveAnalyticsEnv } from "./analyticsEnv";

/**
 * ENG-3404 — Gmail is disconnected BEFORE the account is deleted, and a deletion that would leave it
 * connected does not happen.
 *
 * The Gmail service is its own service, keyed on the Firebase uid, and nothing in `DELETE /account`
 * reaches it. Deleting an account used to leave Haven holding the mailbox's `gmail.modify` grant and
 * scanning it indefinitely. So the console disconnects first, with the person's own token, through
 * the same route Email Scanning's Disconnect uses.
 *
 * How it decides, in order:
 *
 * 1. Ask the service for the connection (`GET /connection`, the call Email Scanning makes).
 * 2. It says not connected: delete. Nothing to disconnect.
 * 3. It says connected, or did not answer: disconnect (`POST /connection/disconnect`, idempotent).
 *    Confirmed: delete.
 * 4. The disconnect failed after the service said "connected": STOP. That is exactly the deletion
 *    that would leave a mailbox scanned for an account that no longer exists.
 * 5. Neither call answered, so whether a connection exists is unknown. Stop only for someone who
 *    could have connected, i.e. the Email Scanning flag is on for them. Everyone else deletes:
 *    connecting was never offered to them. The one case that lets through is a connection made under
 *    a flag since turned off while the service happens to be down, and the Gmail service's daily
 *    sweep disconnects deleted accounts for that.
 *
 * None of it runs unless the console's backend is prod. Alpha and dev consoles call the PROD Gmail
 * service (package.json's deploy scripts) with the same Firebase project, so deleting a test account
 * there would disconnect the tester's real Gmail.
 *
 * Pure apart from the two calls it is handed, so each branch is assertable without a render.
 */

/** How long each of the gate's two calls may take. Scoped to deletion; see GmailRequestOptions. */
export const GMAIL_GATE_TIMEOUT_MS = 15_000;

export interface GmailDeletionApi {
  getConnection: () => Promise<Pick<GmailConnection, "connected" | "gmailEmail">>;
  disconnect: () => Promise<{ disconnected?: boolean; alreadyDisconnected?: boolean }>;
}

export interface GmailDeletionContext {
  emailScanningEnabled: boolean;
  /** The console's backend (`API_BASE_URL`), which says whether this is the prod console. */
  apiBaseUrl: string;
}

export type GmailDeletionBlock =
  /** A connection exists and could not be disconnected. */
  | { proceed: false; reason: "disconnect-failed"; gmailEmail?: string }
  /** The service did not answer, for someone who could have connected. */
  | { proceed: false; reason: "unreachable" };

export type GmailDeletionGate =
  /**
   * `disconnected` only when a mailbox was actually disconnected just now; `unverified`: the service
   * did not answer, for someone never offered Gmail (step 5); `skipped`: not the prod console.
   */
  | { proceed: true; outcome: "not-connected" | "disconnected" | "unverified" | "skipped" }
  | GmailDeletionBlock;

export async function disconnectGmailBeforeDeletion(
  api: GmailDeletionApi,
  { emailScanningEnabled, apiBaseUrl }: GmailDeletionContext,
): Promise<GmailDeletionGate> {
  if (resolveAnalyticsEnv(apiBaseUrl) !== "prod") return { proceed: true, outcome: "skipped" };

  let connected: boolean | null = null;
  let gmailEmail: string | undefined;
  try {
    const status = await api.getConnection();
    // Only a real boolean is an answer: a proxy's 200 or a changed shape is "unknown", not "no".
    if (typeof status?.connected === "boolean") {
      connected = status.connected;
      gmailEmail = typeof status.gmailEmail === "string" && status.gmailEmail ? status.gmailEmail : undefined;
    }
  } catch {
    // No answer. Decided below.
  }
  if (connected === false) return { proceed: true, outcome: "not-connected" };

  let disconnected = false;
  let nothingLinked = false;
  try {
    const result = await api.disconnect();
    disconnected = result?.disconnected === true;
    nothingLinked = result?.alreadyDisconnected === true;
  } catch {
    // Failed. Decided below.
  }
  if (disconnected) return { proceed: true, outcome: nothingLinked ? "not-connected" : "disconnected" };
  if (connected === true) return { proceed: false, reason: "disconnect-failed", gmailEmail };
  if (emailScanningEnabled) return { proceed: false, reason: "unreachable" };
  return { proceed: true, outcome: "unverified" };
}

/**
 * What to tell the person when the deletion stops. Points at Email scanning when they can open it,
 * and otherwise at the Gmail section of Settings, the other place Disconnect lives.
 */
export function describeGmailDeletionBlock(block: GmailDeletionBlock, emailScanningEnabled: boolean): string {
  if (block.reason === "unreachable") {
    return "Your account was not deleted. Haven couldn't reach its email scanning service to make sure " +
      "your Gmail is disconnected. Please try again in a few minutes.";
  }
  const mailbox = block.gmailEmail ? ` (${block.gmailEmail})` : "";
  const where = emailScanningEnabled ? "Email scanning" : "the Gmail section of Settings";
  return `Your account was not deleted. Haven couldn't disconnect your Gmail${mailbox}, and deleting your ` +
    `account now would leave it connected. Please try again, or disconnect Gmail from ${where} first.`;
}

/**
 * What to add when the gate disconnected Gmail and the deletion then did not happen: refused (409),
 * failed, or never reached the server. Without it the person is told only that their account is still
 * there, and finds out later that Gmail scanning stopped.
 */
export function describeGmailDisconnectedAnyway(emailScanningEnabled: boolean): string {
  return "Your Gmail was disconnected before the deletion was attempted, and it stays disconnected." +
    (emailScanningEnabled ? " You can reconnect it from Email scanning." : "");
}
